/**
 * Catálogo único de navegación del panel (desktop, mobile y tiles de /home).
 * Filtra por módulos efectivos + rol (negocio / vendedor).
 */
import { hasFeature, hasAnyFeature, getPlanCodigo } from '@/utils/features';
import { puedeGestionarNegocio, isVendedor, getUsuario } from '@/utils/session';
import { getSidebarPins, SIDEBAR_FIXED_IDS, SIDEBAR_PINS_MAX } from '@/utils/sidebarPins';

/** @typedef {'vender' | 'catalogo' | 'gestion' | 'cuenta'} PanelNavSection */

/**
 * @typedef {object} PanelNavItem
 * @property {string} id
 * @property {string|((ctx: { usuario: string }) => string)} to
 * @property {string} label
 * @property {string} [shortLabel]
 * @property {string} icon
 * @property {PanelNavSection} section
 * @property {string[]} [features] — todos requeridos
 * @property {string[]} [featuresAny] — al menos uno
 * @property {boolean} [gestionOnly]
 * @property {boolean} [vendedorOk] — default true salvo gestionOnly
 * @property {boolean} [onlineOnly]
 * @property {boolean} [offlineAllowed]
 * @property {boolean} [accent]
 * @property {boolean} [tilePos]
 * @property {boolean} [tileAccent]
 * @property {boolean} [tileAnalytics]
 * @property {boolean} [showPedidosBadge]
 * @property {number} [mobilePriority] — menor = más prioritario en bottom bar (después de Inicio)
 * @property {boolean} [mobilePrimary] — candidato a slot fijo de bottom bar
 * @property {boolean} [desktopPrimaryCta] — CTA destacado en desktop
 * @property {number} [desktopPrimaryRank] — menor = preferido como CTA
 */

/** @type {PanelNavItem[]} */
export const PANEL_NAV_ITEMS = [
    {
        id: 'home',
        to: '/u/home',
        label: 'Inicio',
        shortLabel: 'Inicio',
        icon: 'bi-house-door',
        section: 'cuenta',
        onlineOnly: false,
        mobilePriority: 0,
        mobilePrimary: true,
    },
    {
        id: 'pos',
        to: '/u/pos',
        label: 'Cobrar (POS)',
        shortLabel: 'Cobrar',
        icon: 'bi-cash-coin',
        section: 'vender',
        features: ['pos'],
        accent: true,
        tilePos: true,
        mobilePriority: 10,
        mobilePrimary: true,
        desktopPrimaryCta: true,
        desktopPrimaryRank: 1,
        onlineOnly: false,
    },
    {
        id: 'mesas',
        to: '/u/mesas',
        label: 'Mesas',
        shortLabel: 'Mesas',
        icon: 'bi-fork-knife',
        section: 'vender',
        features: ['mesas'],
        mobilePriority: 15,
        mobilePrimary: true,
        onlineOnly: true,
        offlineAllowed: true,
    },
    {
        id: 'caja',
        to: '/u/caja',
        label: 'Caja actual',
        shortLabel: 'Caja',
        icon: 'bi-safe',
        section: 'vender',
        features: ['caja'],
        tileAccent: true,
        mobilePriority: 20,
        mobilePrimary: true,
        desktopPrimaryCta: true,
        desktopPrimaryRank: 3,
        onlineOnly: true,
        offlineAllowed: true,
    },
    {
        id: 'pedido_mostrador',
        to: '/u/pedido-mostrador',
        label: 'Tomar pedido',
        shortLabel: 'Pedido',
        icon: 'bi-cart-plus',
        section: 'vender',
        features: ['pedido_mostrador'],
        mobilePriority: 25,
        mobilePrimary: true,
        desktopPrimaryCta: true,
        desktopPrimaryRank: 2,
        onlineOnly: true,
    },
    {
        id: 'pedidos',
        to: '/u/pedidos',
        label: 'Pedidos',
        shortLabel: 'Pedidos',
        icon: 'bi-receipt-cutoff',
        section: 'vender',
        featuresAny: ['carrito_online', 'pedido_mostrador', 'mesas'],
        showPedidosBadge: true,
        mobilePriority: 30,
        mobilePrimary: true,
        onlineOnly: true,
    },
    {
        id: 'cobros',
        to: '/u/cobros',
        label: 'Ventas del local',
        shortLabel: 'Ventas',
        icon: 'bi-wallet2',
        section: 'vender',
        features: ['pos'],
        mobilePriority: 35,
        mobilePrimary: true,
        onlineOnly: false,
    },
    {
        id: 'comprobantes_arca',
        to: '/u/comprobantes-arca',
        label: 'Facturas ARCA',
        shortLabel: 'Facturas',
        icon: 'bi-receipt',
        section: 'gestion',
        features: ['integracion_arca'],
        gestionOnly: true,
        mobilePriority: 78,
        onlineOnly: true,
    },
    {
        id: 'cajas',
        to: '/u/cajas',
        label: 'Cierres de caja',
        shortLabel: 'Cierres',
        icon: 'bi-clock-history',
        section: 'gestion',
        features: ['caja'],
        gestionOnly: true,
        mobilePriority: 80,
        onlineOnly: true,
    },
    {
        id: 'productos',
        to: '/u/productos',
        label: 'Productos',
        shortLabel: 'Productos',
        icon: 'bi-grid-3x3-gap',
        section: 'catalogo',
        mobilePriority: 40,
        mobilePrimary: true,
        onlineOnly: true,
        offlineAllowed: true,
    },
    {
        id: 'etiquetas',
        to: '/u/etiquetas',
        label: 'Etiquetas de góndola',
        shortLabel: 'Etiquetas',
        icon: 'bi-tags',
        section: 'catalogo',
        features: ['pos'],
        gestionOnly: true,
        mobilePriority: 68,
        onlineOnly: true,
        offlineAllowed: true,
    },
    {
        id: 'stock',
        to: '/u/stock',
        label: 'Movimientos de stock',
        shortLabel: 'Stock',
        icon: 'bi-box-seam',
        section: 'catalogo',
        features: ['stock'],
        mobilePriority: 70,
        onlineOnly: true,
        offlineAllowed: true,
    },
    {
        id: 'publicaciones',
        to: '/u/publicaciones',
        label: 'Publicaciones',
        shortLabel: 'Pubs',
        icon: 'bi-images',
        section: 'catalogo',
        features: ['publicaciones'],
        gestionOnly: true,
        mobilePriority: 75,
        onlineOnly: true,
    },
    {
        id: 'menu_publico',
        to: ({ usuario }) => (usuario ? `/${usuario}` : '/'),
        label: 'Mi tienda',
        shortLabel: 'Tienda',
        icon: 'bi-shop-window',
        section: 'catalogo',
        features: ['menu_online'],
        gestionOnly: true,
        tileAccent: true,
        mobilePriority: 65,
        onlineOnly: true,
    },
    {
        id: 'crear_qr',
        to: '/u/crearqr',
        label: 'QR del menú',
        shortLabel: 'QR',
        icon: 'bi-qr-code',
        section: 'catalogo',
        features: ['menu_online'],
        gestionOnly: true,
        mobilePriority: 85,
        onlineOnly: true,
    },
    {
        id: 'analiticas',
        to: '/u/analiticas',
        label: 'Analíticas',
        shortLabel: 'Analíticas',
        icon: 'bi-graph-up-arrow',
        section: 'gestion',
        features: ['analiticas'],
        gestionOnly: true,
        tileAnalytics: true,
        mobilePriority: 50,
        mobilePrimary: true,
        onlineOnly: true,
    },
    {
        id: 'clientes',
        to: '/u/clientes',
        label: 'Clientes',
        shortLabel: 'Clientes',
        icon: 'bi-people',
        section: 'gestion',
        features: ['clientes'],
        gestionOnly: true,
        mobilePriority: 45,
        mobilePrimary: true,
        onlineOnly: true,
    },
    {
        id: 'ticket',
        to: '/u/ticket',
        label: 'Tickets',
        shortLabel: 'Tickets',
        icon: 'bi-receipt',
        section: 'gestion',
        features: ['tickets'],
        gestionOnly: true,
        mobilePriority: 95,
        onlineOnly: true,
    },
    {
        id: 'dispositivos',
        to: '/u/dispositivos',
        label: 'Dispositivos POS',
        shortLabel: 'Devices',
        icon: 'bi-pc-display',
        section: 'gestion',
        features: ['pos'],
        gestionOnly: true,
        mobilePriority: 96,
        onlineOnly: true,
    },
    {
        id: 'logs',
        to: '/u/logs',
        label: 'Actividad',
        shortLabel: 'Logs',
        icon: 'bi-journal-text',
        section: 'gestion',
        gestionOnly: true,
        mobilePriority: 100,
        onlineOnly: true,
    },
    {
        id: 'perfil',
        to: '/u/modificar',
        label: 'Perfil',
        shortLabel: 'Perfil',
        icon: 'bi-person-circle',
        section: 'cuenta',
        gestionOnly: true,
        mobilePriority: 55,
        onlineOnly: true,
    },
    {
        id: 'cuenta_vendedor',
        to: '/u/modificar',
        label: 'Mi cuenta',
        shortLabel: 'Cuenta',
        icon: 'bi-person-gear',
        section: 'cuenta',
        vendedorOk: true,
        gestionOnly: false,
        /** Solo visible si es vendedor (filtro extra en itemVisible). */
        vendedorOnly: true,
        mobilePriority: 55,
        onlineOnly: true,
    },
];

function featureOk(item) {
    if (item.features && item.features.length) {
        if (!item.features.every((f) => hasFeature(f))) return false;
    }
    if (item.featuresAny && item.featuresAny.length) {
        if (!hasAnyFeature(...item.featuresAny)) return false;
    }
    return true;
}

/**
 * @param {PanelNavItem} item
 * @param {{ online?: boolean, usuario?: string }} [opts]
 */
export function itemVisible(item, opts = {}) {
    const online = opts.online !== false;
    if (item.onlineOnly && !online && !item.offlineAllowed) return false;
    if (!featureOk(item)) return false;

    const gestion = puedeGestionarNegocio();
    const vendedor = isVendedor();

    if (item.vendedorOnly) return vendedor;
    if (item.gestionOnly && !gestion) return false;
    if (item.vendedorOk === false && vendedor) return false;
    return true;
}

/**
 * @param {PanelNavItem} item
 * @param {{ usuario?: string }} [ctx]
 */
export function resolveItemTo(item, ctx = {}) {
    const usuario = ctx.usuario != null ? ctx.usuario : getUsuario();
    if (typeof item.to === 'function') return item.to({ usuario });
    return item.to;
}

/**
 * @param {{ online?: boolean, usuario?: string, section?: PanelNavSection|PanelNavSection[] }} [opts]
 * @returns {Array<PanelNavItem & { href: string }>}
 */
export function getVisibleNavItems(opts = {}) {
    const sections = opts.section
        ? (Array.isArray(opts.section) ? opts.section : [opts.section])
        : null;
    const usuario = opts.usuario != null ? opts.usuario : getUsuario();
    return PANEL_NAV_ITEMS
        .filter((item) => {
            if (sections && !sections.includes(item.section)) return false;
            return itemVisible(item, opts);
        })
        .map((item) => ({
            ...item,
            href: resolveItemTo(item, { usuario }),
        }));
}

/**
 * Items que se pueden fijar como acceso directo (sin Inicio ni Perfil).
 * @param {{ online?: boolean, usuario?: string }} [opts]
 */
export function getPinnableNavItems(opts = {}) {
    return getVisibleNavItems(opts).filter((i) => !SIDEBAR_FIXED_IDS.has(i.id));
}

/**
 * Defaults por tipo de plan (filtrados por módulos visibles).
 * Tienda online → Pedidos, Productos, Publicaciones.
 * Resto → sección Vender (comportamiento histórico).
 * @param {{ online?: boolean, usuario?: string }} [opts]
 */
export function getDefaultSidebarPins(opts = {}) {
    const pinnable = getPinnableNavItems(opts);
    const byId = new Map(pinnable.map((i) => [i.id, i]));
    const pick = (ids) =>
        ids.filter((id) => byId.has(id)).slice(0, SIDEBAR_PINS_MAX);

    const plan = getPlanCodigo();
    const esOnline =
        plan === 'online' ||
        plan === 'tienda_online' ||
        (hasFeature('carrito_online') &&
            !hasFeature('pedido_mostrador') &&
            !hasFeature('pos'));

    if (esOnline) {
        return pick(['pedidos', 'productos', 'publicaciones']);
    }

    return pick(
        getVisibleNavItems({ ...opts, section: 'vender' })
            .filter((i) => !SIDEBAR_FIXED_IDS.has(i.id))
            .map((i) => i.id),
    );
}

/**
 * Ids efectivos de pins (custom o default), filtrados por módulos/visibilidad.
 * @param {{ online?: boolean, usuario?: string, pins?: string[]|null }} [opts]
 */
export function resolveSidebarPinIds(opts = {}) {
    const pinnable = getPinnableNavItems(opts);
    const byId = new Map(pinnable.map((i) => [i.id, i]));
    const raw = opts.pins !== undefined ? opts.pins : getSidebarPins();
    const source = Array.isArray(raw) ? raw : getDefaultSidebarPins(opts);
    const resolved = [];
    const seen = new Set();
    for (const id of source) {
        if (!byId.has(id) || seen.has(id)) continue;
        resolved.push(id);
        seen.add(id);
        if (resolved.length >= SIDEBAR_PINS_MAX) break;
    }
    return resolved;
}

/**
 * Accesos directos del sidebar de escritorio (iconos sueltos).
 * @param {{ online?: boolean, usuario?: string, pins?: string[]|null }} [opts]
 */
export function getDesktopPinnedItems(opts = {}) {
    const all = getVisibleNavItems(opts);
    const byId = new Map(all.map((i) => [i.id, i]));
    return resolveSidebarPinIds(opts)
        .map((id) => byId.get(id))
        .filter(Boolean);
}

/**
 * Items no fijados de una sección (para flyouts).
 * @param {PanelNavSection} section
 * @param {{ online?: boolean, usuario?: string, pins?: string[]|null }} [opts]
 */
export function getDesktopFlyoutItems(section, opts = {}) {
    const pinned = new Set(resolveSidebarPinIds(opts));
    return getVisibleNavItems({ ...opts, section })
        .filter((i) => !SIDEBAR_FIXED_IDS.has(i.id) && !pinned.has(i.id));
}

/**
 * CTA primario de escritorio: Cobrar > Tomar pedido > Caja.
 * @param {{ online?: boolean }} [opts]
 */
export function getDesktopPrimaryCta(opts = {}) {
    const pinnedIds = new Set(resolveSidebarPinIds(opts));
    const candidates = getVisibleNavItems(opts)
        .filter((i) => i.desktopPrimaryCta && i.id !== 'home' && pinnedIds.has(i.id))
        .sort((a, b) => (a.desktopPrimaryRank || 99) - (b.desktopPrimaryRank || 99));
    if (candidates[0]) return candidates[0];
    return getVisibleNavItems(opts)
        .filter((i) => i.desktopPrimaryCta && i.id !== 'home')
        .sort((a, b) => (a.desktopPrimaryRank || 99) - (b.desktopPrimaryRank || 99))[0] || null;
}

/**
 * Items de dropdowns desktop por sección (sin home ni CTAs sueltos de cuenta).
 * @deprecated Preferí getDesktopFlyoutItems / getDesktopPinnedItems.
 */
export function getDesktopSectionItems(section, opts = {}) {
    return getVisibleNavItems({ ...opts, section }).filter((i) => i.id !== 'home');
}

function isEquiposMobileProfile() {
    return hasFeature('pedido_mostrador') && !hasFeature('pos');
}

function isGastroOrIntegralMobileProfile() {
    const plan = getPlanCodigo();
    return (
        plan === 'gastronomia' ||
        plan === 'integral' ||
        plan === 'completo' ||
        plan === 'legacy_full'
    );
}

/**
 * Bottom bar: Inicio + hasta `maxPrimary` slots + el resto va a «Más».
 * Offline+POS: Inicio, Cobrar, Ventas POS (sin Más).
 * Usa pins personalizados como orden preferido cuando existen.
 * @param {{ online?: boolean, usuario?: string, maxPrimary?: number, pins?: string[]|null }} [opts]
 */
export function getMobileBottomSlots(opts = {}) {
    const online = opts.online !== false;
    const all = getVisibleNavItems(opts);
    const byId = (id) => all.find((i) => i.id === id);

    if (!online) {
        const slots = [byId('home'), byId('pos'), byId('productos'), byId('stock'), byId('cobros')]
            .filter(Boolean);
        return { slots, more: [] };
    }

    const maxPrimary = opts.maxPrimary != null
        ? opts.maxPrimary
        : (hasFeature('pos') && !isGastroOrIntegralMobileProfile() ? 3 : 4);

    const home = byId('home');
    const primaryPool = all.filter((i) => {
        if (SIDEBAR_FIXED_IDS.has(i.id) && i.id !== 'home') return false;
        if (i.id === 'home') return false;
        if (isEquiposMobileProfile() && i.id === 'caja') return false;
        if (isGastroOrIntegralMobileProfile() && i.id === 'pos') return false;
        return true;
    });
    const preferredIds = resolveSidebarPinIds(opts);
    const ranked = [];
    const seen = new Set();

    for (const id of preferredIds) {
        const item = primaryPool.find((i) => i.id === id);
        if (item && !seen.has(item.id)) {
            ranked.push(item);
            seen.add(item.id);
        }
    }
    for (const item of primaryPool) {
        if (!seen.has(item.id) && item.mobilePrimary) {
            ranked.push(item);
            seen.add(item.id);
        }
    }
    for (const item of primaryPool) {
        if (!seen.has(item.id)) {
            ranked.push(item);
            seen.add(item.id);
        }
    }

    const primaries = ranked.slice(0, maxPrimary);
    const slots = [home, ...primaries].filter(Boolean);
    const slotIds = new Set(slots.map((s) => s.id));
    const more = all
        .filter((i) => !slotIds.has(i.id))
        .sort((a, b) => (a.mobilePriority || 99) - (b.mobilePriority || 99));

    return { slots, more };
}

/**
 * Texto contextual para el lead de /home.
 */
export function getHomeLeadText() {
    if (hasFeature('mesas') && hasFeature('menu_online')) {
        return 'Mesas, menú QR, cobrar en el local y pedidos por WhatsApp.';
    }
    if (hasFeature('pos') && hasFeature('menu_online')) {
        return 'POS, caja y pedidos del menú online desde el mismo panel.';
    }
    if (hasFeature('pos')) {
        return 'Cobrá en el mostrador y controlá caja y ventas del local.';
    }
    if (hasFeature('pedido_mostrador')) {
        return 'Tomá pedidos con el equipo, caja y reportes.';
    }
    if (hasFeature('menu_online') || hasFeature('carrito_online')) {
        return 'Catálogo, pedidos por WhatsApp y tu menú público.';
    }
    return 'Gestioná tu negocio desde acá.';
}

/** Secciones del home (orden de UI). */
export const HOME_SECTIONS = [
    { id: 'vender', title: 'Vender', icon: 'bi-cash-stack' },
    { id: 'catalogo', title: 'Catálogo', icon: 'bi-grid-3x3-gap' },
    { id: 'gestion', title: 'Gestión', icon: 'bi-gear' },
];

/**
 * Items para tiles de home por sección (sin cuenta; perfil se agrega aparte).
 */
export function getHomeTilesBySection(opts = {}) {
    const result = {};
    for (const sec of HOME_SECTIONS) {
        let items = getDesktopSectionItems(sec.id, opts);
        if (sec.id === 'gestion') {
            // Incluir perfil en gestión para dueños
            const perfil = getVisibleNavItems({ ...opts, section: 'cuenta' })
                .find((i) => i.id === 'perfil');
            if (perfil) items = [...items, perfil];
        }
        result[sec.id] = items;
    }
    return result;
}

export function sectionHasItems(section, opts = {}) {
    return getHomeTilesBySection(opts)[section]?.length > 0;
}
